import React from 'react';
import {
  CircularProgress,
  makeStyles,
  Paper,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';

import { TableProps } from './types';

import { primaryBlue, primaryColor } from 'src/data/colors';

import Cell from './tableCell';

interface StylesProps {
  perPage: number;
  equalColumns: boolean;
  hidePagination: boolean;
}

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: '10px',
    width: '100%',
  },
  headContainer: {
    borderRadius: '10px',
  },
  head: {
    backgroundColor: primaryBlue,
    color: 'white',
  },
  firstHeader: {
    borderTopLeftRadius: '10px',
    width: (props: StylesProps) => (props.equalColumns ? '' : '50px'),
  },
  lastHeader: {
    borderTopRightRadius: '10px',
    width: (props: StylesProps) => (props.equalColumns ? '' : '70px'),
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    padding: '10px',
  },
  chip: {
    margin: '5px',
    color: 'white',
  },
  colorProgress: {
    color: primaryColor,
  },
  tableContainer: {
    height: (props: StylesProps) =>
      props.hidePagination ? '' : `calc(48px * ${props.perPage} + 56px)`,
    maxWidth: '100%',
    overflow: 'auto',
  },
}));

const Table: React.FC<TableProps> = ({
  actions,
  chipsColor,
  columns,
  data,
  emptyStateMessage,
  equalColumns = false,
  handleChangePage,
  isLoading,
  pageNumber = 1,
  perPage,
  hidePagination = false,
  total,
}) => {
  const theme = useTheme();
  const isSmallPortrait = useMediaQuery(
    `${theme.breakpoints.down('xs')} and (orientation: portrait)`
  );
  const isSmallLandscape = useMediaQuery(
    `${theme.breakpoints.down('sm')} and (orientation: landscape)`
  );
  const isSmallScreen = isSmallPortrait || isSmallLandscape;

  const classes = useStyles({ perPage, equalColumns, hidePagination });

  return (
    <Paper className={classes.root}>
      <TableContainer className={classes.tableContainer}>
        <MuiTable
          aria-label="sticky table"
          style={{ tableLayout: isSmallScreen ? 'unset' : 'fixed' }}
        >
          <TableHead className={classes.headContainer}>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  className={`
                    ${classes.head}
                    ${index === 0 ? classes.firstHeader : ''}
                    ${index === columns.length - 1 ? classes.lastHeader : ''}
                  `}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 ? (
              <>
                {data.map((row, index) => {
                  return (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                      {columns.map((column, colIndex) => {
                        return (
                          <TableCell key={column.id}>
                            <Cell
                              actions={actions}
                              chipsColor={chipsColor}
                              column={column}
                              id={data[index].id || data[index].userId || 0}
                              row={row}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  {isLoading ? (
                    <CircularProgress classes={{ colorPrimary: classes.colorProgress }} />
                  ) : (
                    emptyStateMessage
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {!hidePagination && (
        <Pagination
          className={classes.pagination}
          count={Math.floor(total / perPage)}
          onChange={handleChangePage}
          page={pageNumber}
        />
      )}
    </Paper>
  );
};

export default Table;
